import React, { createContext, useContext, useState, useCallback } from 'react';
import useSearchExecutor from "@spa/features/search_job/pc/hooks/useSearchExecuter";

const SearchContext = createContext(null);

export const SearchProvider = ({
    children,
    initialKeyword = '',
    initialLocation = '',
    isFromPaidJob = false,
    isFromCrawler = false,
    searchedKeyword = null,
    searchedLocation = null,
    narrowSearchParamsViewModel,
    distanceDefaultValue,
    keywordPlaceholder,
    locationPlaceholder,
    onExecuteAnalytics
}) => {
    const [keyword, setKeyword] = useState(initialKeyword);
    const [location, setLocation] = useState(initialLocation);
    const [locationSuggest, setLocationSuggest] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    const { executeSearch: execSearch } = useSearchExecutor({
        isFromPaidJob,
        isFromCrawler,
        searchedKeyword,
        searchedLocation,
        narrowSearchParamsViewModel,
        distanceDefaultValue,
        onExecuteAnalytics
    });

    const getActiveFilterParams = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        const filterParams = new URLSearchParams();

        const filterKeys = [
            narrowSearchParamsViewModel.jobTypeParamKey,
            narrowSearchParamsViewModel.salaryTypeParamKey,
            narrowSearchParamsViewModel.salaryMinParamKey,
            narrowSearchParamsViewModel.salaryMaxParamKey,
            narrowSearchParamsViewModel.datePostedParamKey,
            narrowSearchParamsViewModel.distanceParamKey,
            narrowSearchParamsViewModel.featureParamKey
        ];

        filterKeys.forEach(key => {
            const value = params.get(key);
            if (value) {
                filterParams.append(key, value);
            }
        });

        return filterParams;
    }, [narrowSearchParamsViewModel]);

    const handleSearch = useCallback(async (params, shouldTrack = true) => {
        setIsSearching(true);
        try {
            let searchParams;

            if (params?.preventDefault) {
                params.preventDefault();
                searchParams = getActiveFilterParams();
            } else {
                searchParams = params.searchParams;
            }

            await execSearch({
                keywordSearch: keyword,
                locationSearch: location,
                locationSuggest: locationSuggest,
                searchParams: searchParams
            });

            // 一部の検索結果ページでは、検索実行の計測を行わない（フィルターからの検索等）
            if (shouldTrack) {
                onExecuteAnalytics();
            }
        } catch (error) {
            console.error('Search failed:', error);
        } finally {
            setIsSearching(false);
        }
    }, [execSearch, getActiveFilterParams, keyword, location, locationSuggest, onExecuteAnalytics]);

    const handleKeywordChange = useCallback((e) => {
        setKeyword(e.target.value);
    }, []);

    const handleLocationChange = useCallback((e) => {
        setLocation(e.target.value);
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            handleSearch(e);
        }
    }, [handleSearch]);

    const value = {
        keyword,
        location,
        locationSuggest,
        isSearching,
        keywordPlaceholder,
        locationPlaceholder,
        setKeyword,
        setLocation,
        setLocationSuggest,
        handleSearch,
        handleKeywordChange,
        handleLocationChange,
        handleKeyDown
    };

    return (
        <SearchContext.Provider value={value}>
            {children}
        </SearchContext.Provider>
    );
};

export const useSearch = () => {
    const context = useContext(SearchContext);
    if (!context) {
        throw new Error('useSearch must be used within a SearchProvider');
    }
    return context;
};
